import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, Scale, FileText, Clock, TrendingDown, AlertTriangle, CheckCircle, Users } from 'lucide-react';

const FCAMotorFinanceSchemePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                FCA Motor Finance Compensation Scheme
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Understanding the FCA's approach to motor finance redress
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                The Financial Conduct Authority (FCA) has been conducting an extensive review of the 
                motor finance industry, with particular focus on <strong>Discretionary Commission Arrangements 
                (DCAs)</strong> that were used until January 2021. This investigation has significant implications 
                for consumers who may have been overcharged on their car finance.
              </p>
            </div>
          </div>
        </section>

        {/* Background Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Background to the Investigation
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                The FCA's investigation stems from concerns about how commission was earned by car dealers 
                when arranging finance for customers. Under DCAs, dealers could increase the interest rate 
                on your finance to earn more commission – <strong className="text-foreground">often without your knowledge</strong>.
              </p>

              <Card className="bg-gold/5 border-gold">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    Following a Court of Appeal judgment in October 2024, which found that such arrangements 
                    <strong> breached the dealer's fiduciary duty to customers</strong>, the FCA expanded its review to 
                    determine the full extent of consumer harm.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Key Findings Section */}
        <section className="py-16 lg:py-20 bg-destructive/5">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Key Findings
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                The FCA's work has revealed significant concerns:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-l-4 border-l-destructive">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Widespread Use of DCAs</h3>
                    <p className="text-muted-foreground">
                      These arrangements were common across the motor finance industry before the 2021 ban.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Consumer Detriment</h3>
                    <p className="text-muted-foreground">
                      Affected consumers may have paid significantly more interest than necessary.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="text-center border-destructive/50">
                  <CardContent className="p-8">
                    <div className="text-4xl font-bold text-destructive mb-3">£7.5bn</div>
                    <p className="text-foreground font-medium mb-2">Estimated Total Redress</p>
                    <p className="text-sm text-muted-foreground">
                      The total cost to lenders according to FCA estimates
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center border-gold/50">
                  <CardContent className="p-8">
                    <div className="text-4xl font-bold text-gold mb-3">£830</div>
                    <p className="text-foreground font-medium mb-2">Average Compensation</p>
                    <p className="text-sm text-muted-foreground">
                      Per affected agreement, though this varies significantly
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FCA's Approach Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The FCA's Approach
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                The FCA is working to ensure affected consumers can obtain redress efficiently and fairly:
              </p>

              <div className="space-y-6">
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-2">Paused Complaint Deadline</h3>
                        <p className="text-muted-foreground">
                          The FCA has paused the deadline for consumers to make complaints about motor finance 
                          commission. This gives time for the FCA to complete its review and for a fair process 
                          to be established.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                        <Scale className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-2">Potential Redress Scheme</h3>
                        <p className="text-muted-foreground">
                          The FCA is considering whether to establish a formal consumer redress scheme. Such a 
                          scheme could provide a standardised process for assessing and paying compensation to 
                          affected consumers.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-2">Industry Engagement</h3>
                        <p className="text-muted-foreground">
                          The FCA is working with lenders and other stakeholders to determine the most effective 
                          way to deliver redress to consumers.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* What This Means Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <AlertTriangle className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  What This Means for Consumers
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-8">
                If you took out PCP or HP car finance between <strong className="text-primary-foreground">April 2007 and January 2021</strong>, you may be 
                affected by the FCA's investigation. Key points to understand:
              </p>

              <div className="space-y-4">
                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-primary-foreground mb-2">You May Be Entitled to Compensation</h3>
                    <p className="text-primary-foreground/80">
                      If DCAs were used in your finance agreement and you were not properly informed.
                    </p>
                  </div>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-primary-foreground mb-2">No Immediate Deadline</h3>
                    <p className="text-primary-foreground/80">
                      The FCA has paused complaint deadlines while the review is ongoing.
                    </p>
                  </div>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-primary-foreground mb-2">Individual Assessment</h3>
                    <p className="text-primary-foreground/80">
                      Each case will be assessed based on its specific circumstances.
                    </p>
                  </div>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-primary-foreground mb-2">No Guaranteed Outcome</h3>
                    <p className="text-primary-foreground/80">
                      Not everyone will be entitled to compensation – it depends on your individual case.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How to Proceed Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                How to Proceed
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                While the FCA's work is ongoing, you can still take steps to understand your position:
              </p>

              <div className="space-y-4">
                {[
                  { step: "1", title: "Gather Information", desc: "Collect any documents you have relating to your car finance agreement" },
                  { step: "2", title: "Seek Professional Advice", desc: "A regulated law firm can assess your circumstances and advise on your options" },
                  { step: "3", title: "Register Your Interest", desc: "Having your case assessed now means you'll be prepared when the FCA's process is finalised" }
                ].map((item, idx) => (
                  <Card key={idx} className="border-l-4 border-l-accent">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0 text-white font-bold">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Act Now */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Why Act Now?
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                Although the FCA has paused complaint deadlines, there are benefits to having your 
                case assessed early:
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Gather evidence while it's still available",
                  "Understand your potential entitlement",
                  "Be prepared for when the FCA's process concludes",
                  "Receive professional guidance throughout the process"
                ].map((item, idx) => (
                  <Card key={idx} className="border-border">
                    <CardContent className="p-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                      <p className="text-foreground">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Blue Lion Law's Role */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                Blue Lion Law's Role
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                Blue Lion Law helps clients navigate the motor finance claims process. We:
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Assess individual circumstances to determine potential eligibility",
                  "Gather necessary evidence and documentation",
                  "Handle communications with lenders and other parties",
                  "Keep clients informed of developments in FCA guidance",
                  "Work to secure fair compensation where claims are valid"
                ].map((item, idx) => (
                  <Card key={idx} className="border-l-4 border-l-accent">
                    <CardContent className="p-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <p className="text-foreground">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gold/5 border-gold">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    We operate on a <strong>no win, no fee basis</strong> for eligible claims, meaning you won't pay 
                    unless your claim is successful.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Get Your Assessment
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Don't wait for the FCA's review to conclude. Understand your position now.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                    Find Your Agreements
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="tel:02081530033">
                    Call 0208 153 0033
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/pcp/how-pcp-is-mis-sold" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How PCP is Mis-sold</span>
                </a>
                <a href="/pcp/pcp-claims-faq" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">PCP Claims FAQ</span>
                </a>
                <a href="/pcp-car-finance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">PCP Car Finance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FCAMotorFinanceSchemePage;
